import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Package, Send, Download, Trash2, RefreshCw, Search,
  Calendar, FileArchive, User, ShieldOff, CheckCircle, Loader2,
  AlertCircle, MessageSquare
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getToken } from '../../services/tokenService';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Tableau de bord des partages ZIP.
 * - mode='sent' (Membre) : "Mes partages envoyés" avec révocation
 * - mode='received' (Cooper) : "Partages reçus" avec téléchargement
 */
export default function ZipSharesDashboard({ mode = 'sent' }) {
  const { getAuthHeader } = useAuth();
  const isSent = mode === 'sent';
  const endpoint = isSent ? '/api/ged/zip-shares/me' : '/api/ged/zip-shares/received';

  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | actif | revoque
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    fetchShares();
    // eslint-disable-next-line
  }, []);

  const fetchShares = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}${endpoint}`, { headers: getAuthHeader() });
      setShares(response.data?.shares || []);
    } catch {
      toast.error('Erreur lors du chargement des partages');
      setShares([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (share) => {
    if (share.statut === 'revoque') {
      toast.error('Ce partage a été révoqué et n\'est plus accessible.');
      return;
    }
    const url = `${BACKEND_URL}/api/ged/download/${share.zip_doc_id}?token=${encodeURIComponent(getToken())}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = share.zip_filename || 'partage.zip';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/ged/zip-shares/${revokeTarget.id}/revoke`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success('Partage révoqué');
      setRevokeOpen(false);
      setRevokeTarget(null);
      fetchShares();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la révocation');
    } finally {
      setRevoking(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} Go`;
  };

  const filtered = useMemo(() => {
    return shares.filter((s) => {
      if (statusFilter !== 'all' && s.statut !== statusFilter) return false;
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      const counterparty = (isSent ? s.cooper : s.member) || {};
      return (
        (s.zip_filename || '').toLowerCase().includes(q) ||
        (s.message || '').toLowerCase().includes(q) ||
        (counterparty.nom || '').toLowerCase().includes(q) ||
        (counterparty.email || '').toLowerCase().includes(q)
      );
    });
  }, [shares, searchTerm, statusFilter, isSent]);

  const stats = useMemo(() => ({
    total: shares.length,
    actifs: shares.filter((s) => s.statut === 'actif').length,
    revoques: shares.filter((s) => s.statut === 'revoque').length,
    totalSize: shares.reduce((acc, s) => acc + (s.zip_size || 0), 0),
  }), [shares]);

  return (
    <div className="space-y-6" data-testid={`zip-shares-${mode}-dashboard`}>
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900 p-6 lg:p-7 text-white shadow-2xl">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(99,102,241,0.4),_transparent_55%)] pointer-events-none" />
        <div className="relative flex flex-col xl:flex-row items-start xl:items-center justify-between gap-5">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-lg flex-shrink-0">
              {isSent ? <Send className="h-7 w-7 text-white" /> : <Package className="h-7 w-7 text-white" />}
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-200 border border-indigo-400/30 backdrop-blur-sm font-semibold text-xs flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse" />
                  Audit RGPD
                </span>
                <span className="px-2 py-0.5 rounded-full bg-white/10 text-white border border-white/20 backdrop-blur-sm font-semibold text-xs">
                  {stats.total} partage{stats.total > 1 ? 's' : ''}
                </span>
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold">
                {isSent ? 'Mes partages envoyés' : 'Partages reçus'}
              </h2>
              <p className="text-indigo-100 mt-1 text-sm lg:text-base">
                {isSent
                  ? 'Historique des dossiers ZIP transmis à votre COOPER\'S. Vous pouvez révoquer un partage à tout moment.'
                  : 'Dossiers ZIP partagés par vos membres adhérents — téléchargez-les pour traitement.'}
              </p>
            </div>
          </div>
          <Button
            onClick={fetchShares}
            variant="outline"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20"
            data-testid="zip-shares-refresh"
          >
            <RefreshCw className="h-4 w-4 mr-2" /> Actualiser
          </Button>
        </div>

        <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
          <KpiCard icon={Package} label="Total" value={stats.total} color="indigo" />
          <KpiCard icon={CheckCircle} label="Actifs" value={stats.actifs} color="emerald" />
          <KpiCard icon={ShieldOff} label="Révoqués" value={stats.revoques} color="rose" />
          <KpiCard icon={FileArchive} label="Volume" value={formatSize(stats.totalSize)} color="cyan" />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder={isSent ? 'Rechercher (nom ZIP, COOPER\'S, message…)' : 'Rechercher (nom ZIP, membre, message…)'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="zip-shares-search"
          />
        </div>
        <div className="flex gap-1">
          {[
            { key: 'all', label: 'Tous' },
            { key: 'actif', label: 'Actifs' },
            { key: 'revoque', label: 'Révoqués' },
          ].map((f) => (
            <Button
              key={f.key}
              variant={statusFilter === f.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(f.key)}
              className={statusFilter === f.key ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
              data-testid={`zip-shares-filter-${f.key}`}
            >
              {f.label}
            </Button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-indigo-600 animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="py-12 text-center">
            <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'Aucun partage trouvé' : (isSent ? 'Aucun partage envoyé' : 'Aucun partage reçu')}
            </h3>
            <p className="text-gray-500">
              {isSent
                ? 'Constituez un dossier ZIP depuis l\'onglet Photos/Vidéos pour partager des fichiers avec votre COOPER\'S.'
                : 'Aucun de vos membres ne vous a encore partagé de dossier ZIP.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="zip-shares-list">
          {filtered.map((s) => {
            const cp = (isSent ? s.cooper : s.member) || {};
            const isRevoked = s.statut === 'revoque';
            return (
              <Card
                key={s.id}
                className={`overflow-hidden transition-shadow hover:shadow-md ${isRevoked ? 'opacity-60' : ''}`}
                data-testid={`zip-share-row-${s.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isRevoked ? 'bg-rose-100 text-rose-600' : 'bg-indigo-100 text-indigo-600'
                      }`}>
                        <FileArchive className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-semibold text-gray-900 truncate">{s.zip_filename}</h3>
                          <Badge className={isRevoked ? 'bg-rose-100 text-rose-700 border-rose-200' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}>
                            {isRevoked ? 'Révoqué' : 'Actif'}
                          </Badge>
                          <Badge variant="outline" className="text-[10px]">
                            {s.included_count} fichier{s.included_count > 1 ? 's' : ''}
                          </Badge>
                          {!s.shared_with_cooper && !isRevoked && (
                            <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300">
                              Sans destinataire
                            </Badge>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600">
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" /> {isSent ? 'Vers : ' : 'De : '}<strong className="text-gray-800">{cp.nom || '—'}</strong>
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" /> {formatDate(s.sent_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <FileArchive className="h-3 w-3" /> {formatSize(s.zip_size)}
                          </span>
                          {isRevoked && (
                            <span className="flex items-center gap-1 text-rose-600">
                              <ShieldOff className="h-3 w-3" /> Révoqué le {formatDate(s.revoked_at)}
                            </span>
                          )}
                        </div>
                        {s.message && (
                          <p className="mt-2 text-sm text-gray-700 bg-gray-50 rounded-md px-3 py-2 flex items-start gap-2">
                            <MessageSquare className="h-3.5 w-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                            <span className="italic">{s.message}</span>
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 self-end lg:self-center">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isRevoked}
                        onClick={() => handleDownload(s)}
                        className="gap-1.5"
                        data-testid={`zip-share-download-${s.id}`}
                      >
                        <Download className="h-4 w-4" /> Télécharger
                      </Button>
                      {isSent && !isRevoked && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => { setRevokeTarget(s); setRevokeOpen(true); }}
                          className="gap-1.5 text-rose-600 border-rose-200 hover:bg-rose-50"
                          data-testid={`zip-share-revoke-${s.id}`}
                        >
                          <Trash2 className="h-4 w-4" /> Révoquer
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Revoke confirmation */}
      <Dialog open={revokeOpen} onOpenChange={(o) => !revoking && setRevokeOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-600">
              <AlertCircle className="h-5 w-5" /> Révoquer ce partage ?
            </DialogTitle>
            <DialogDescription>
              Le dossier <strong>{revokeTarget?.zip_filename}</strong> sera supprimé de la GED et de la pièce jointe du message. L'entrée d'audit est conservée pour traçabilité RGPD.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRevokeOpen(false)} disabled={revoking}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevoke}
              disabled={revoking}
              data-testid="zip-share-confirm-revoke"
            >
              {revoking ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Révocation…</> : <><ShieldOff className="h-4 w-4 mr-2" /> Révoquer</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const KPI_COLORS = {
  indigo: 'text-indigo-300',
  emerald: 'text-emerald-300',
  rose: 'text-rose-300',
  cyan: 'text-cyan-300',
};

const KpiCard = ({ icon: Icon, label, value, color }) => (
  <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-4">
    <div className={`flex items-center gap-2 ${KPI_COLORS[color] || 'text-white'} text-xs font-semibold uppercase tracking-wider`}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </div>
    <p className="text-2xl lg:text-3xl font-bold mt-1">{value}</p>
  </div>
);
